import { Checkbox } from '@almbrand/checkbox';
import { LoadingSpinner } from '@almbrand/loadingspinner';
import classNames from 'classnames';
import { ThemeNames } from 'constantsValues';
import { PageContext } from 'context/PageContext';
import { useSpdSearch } from 'hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import { FieldValues, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { Obj } from 'src/hooks/useSpdSearch';
import { getResponseValueByParam } from 'utilities';
import { CarMakeAndModel } from './CarMakeAndModel';
import styles from './CarSearch.module.scss';
import { LicensePlateInput } from './LicensePlateInput';
import { ResultBox } from './ResultBox';
import { getCarSearchIsValidFieldName, isSearchResultValid, setCarSearchIsValid } from './validateCarSearch';

export type Option = {
	value: string;
	label: string;
};

export type ResultBoxProps = {
	header: string;
	subHeader: string;
};

export interface CarSearchProps {
	register: UseFormRegister<FieldValues>;
	trigger?: UseFormTrigger<FieldValues>;
	setValue?: UseFormSetValue<FieldValues>;
	submitStep?: () => void;
	contentType: string;
	stepContentId: number;
	onComplete?: (licensePlate: string) => void;
	resultBox?: ResultBoxProps | null;
	licensePlateSearchField: SpdSearchType;
	makeSearchField: SpdSearchType;
	yearSearchField: SpdSearchType;
	modelSearchField: SpdSearchType;
	variantSearchField: SpdSearchType;
}

export const CarSearch: React.FC<CarSearchProps> = ({
	register,
	trigger,
	setValue,
	submitStep,
	contentType,
	stepContentId,
	onComplete,
	licensePlateSearchField,
	makeSearchField,
	yearSearchField,
	modelSearchField,
	variantSearchField,
	...props
}) => {
	const pageContext = useContext(PageContext);
	const { theme } = pageContext ?? {};
	const themeName = ThemeNames[theme];

	const licensePlateSearch = useSpdSearch({ spdSearchType: licensePlateSearchField });

	const [showSpinner, setShowSpinner] = useState(false);
	const [showResult, setShowResult] = useState(false);
	const [noLicensePlate, setNoLicensePlate] = useState(false);
	const [resultBox, setResultBox] = useState<ResultBoxProps | null>(null);
	const licensePlateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const carSearchIsValidFieldName = getCarSearchIsValidFieldName(stepContentId);

	const updateCarSearchIsValid = (isValid: boolean) => {
		setCarSearchIsValid({
			fieldName: carSearchIsValidFieldName,
			isValid,
			register,
			setValue,
			trigger,
		});
	};

	useEffect(() => {
		updateCarSearchIsValid(false);

		return () => {
			if (licensePlateDebounceRef.current) {
				clearTimeout(licensePlateDebounceRef.current);
			}
		};
	}, []);

	// Show ResultBox and hide spinner when resultBox is set from parent
	useEffect(() => {
		if (resultBox) {
			setShowResult(true);
			setShowSpinner(false);
		}
	}, [resultBox]);

	const handleLicensePlateChange = async (formatted: string, isComplete: boolean) => {
		// Remove all spaces and make lower case before sending to parent
		const normalized = formatted.replace(/\s+/g, '').toLowerCase();

		if (licensePlateDebounceRef.current) {
			clearTimeout(licensePlateDebounceRef.current);
			licensePlateDebounceRef.current = null;
		}

		if (isComplete) {
			setShowSpinner(true);
			setShowResult(false);
			updateCarSearchIsValid(false);

			licensePlateDebounceRef.current = setTimeout(async () => {
				const result = await licensePlateSearch.doSearch({
					name: licensePlateSearchField.searchConfiguration.spdSearchConfigurationName,
					params: [
						{
							param_name:
								licensePlateSearchField.searchConfiguration.inputFields.items[0].content.param || '',
							param_value: normalized.toLocaleUpperCase(),
						},
						{
							param_name: 'USER_ID',
							param_value: ' ',
						},
					],
				});

				if (!isSearchResultValid(licensePlateSearchField.searchConfiguration, result as Obj)) {
					setShowSpinner(false);
					setShowResult(false);
					setResultBox(null);
					updateCarSearchIsValid(false);
					return;
				}

				setResultBox({
					header: `${(result as Obj).make} ${(result as Obj).model}`,
					subHeader: `${(result as Obj).modelYear} ${(result as Obj).variant}`,
				});

				saveFormValue(licensePlateSearchField.uniqueIdentifier.uid, normalized.toLocaleUpperCase());
				saveOutputFieldsToForm(
					register,
					licensePlateSearchField.searchConfiguration,
					result as Obj,
					contentType,
					true
				);
			}, 500);

			onComplete?.(normalized);
		} else {
			setShowSpinner(false);
			setShowResult(false);
			setResultBox(null);
			updateCarSearchIsValid(false);
		}
	};

	const saveFormValue = (fieldName: string, value: unknown) => {
		const { onChange, name } = register(fieldName, { required: true });
		const formValue = {
			id: name,
			value,
			type: contentType,
		};

		if (setValue) {
			setValue(name, formValue, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
			return;
		}

		onChange({
			target: {
				name,
				value: formValue,
			},
		});
	};

	const saveOutputFieldsToForm = (
		register: UseFormRegister<FieldValues>,
		searchConfiguration: SpdSearchType['searchConfiguration'],
		value: Option | Obj,
		contentType: string,
		completeCarSearch?: boolean
	) => {
		searchConfiguration.outputFields.items.forEach((outputField) => {
			const mappedValue = value.value ?? getResponseValueByParam(value as Obj, outputField.content.param);
			saveFormValue(outputField.content.uniqueIdentifier.uid, mappedValue);
		});

		if (completeCarSearch) {
			updateCarSearchIsValid(isSearchResultValid(searchConfiguration, value));
		}
	};

	const handleCheckboxChange = async () => {
		setNoLicensePlate(!noLicensePlate);
		setShowResult(false);
		setResultBox(null);
		setShowSpinner(false);
		updateCarSearchIsValid(false);
	};

	return (
		<div className={classNames(styles.CarSearch)}>
			{!noLicensePlate && <LicensePlateInput onChange={handleLicensePlateChange} />}

			{showSpinner && !noLicensePlate && (
				<div className={classNames(styles.CarSearch__spinner)}>
					<LoadingSpinner
						color='primary'
						style='small'
					/>
				</div>
			)}

			{showResult && resultBox && !noLicensePlate && <ResultBox {...resultBox} />}

			<div className={classNames(styles.CarSearch__checkbox)}>
				<Checkbox
					name='checkbox'
					label='Jeg kender ikke min nummerplade'
					roundedCorners
					onChange={handleCheckboxChange}
				/>
			</div>

			{noLicensePlate && (
				<CarMakeAndModel
					contentType={contentType}
					makeSearchField={makeSearchField}
					yearSearchField={yearSearchField}
					modelSearchField={modelSearchField}
					variantSearchField={variantSearchField}
					themeName={themeName}
					saveOutputFieldsToForm={saveOutputFieldsToForm}
					register={register}
				/>
			)}
		</div>
	);
};
